// Types pour la validation des données d'entrée
export interface MatchInputData {
  matchId: string;
  competitionName: string;
  categoryName: string;
  drawName: string;
  refereeName?: string; // Optionnel
  dateTime: string; // Format: "YYYY-MM-DD HH:MM"
  scoringType: "BestOf5" | "BestOf3";
  playerA: string;
  playerB: string;
  playerAAvatar?: string; // URL avatar joueur A (facultatif)
  playerBAvatar?: string; // URL avatar joueur B (facultatif)
  resultCallbackUrl: string; // URL de callback des résultats
  redirectUrl?: string; // URL facultative pour redirection utilisateur
  roundType?: string; // Type de tour (affiché dans l'annonce de début de match)
  // Champs obligatoires uniquement si liveScoreUrl est renseigné
  liveScoreUrl?: string; // URL pour transmettre les scores en live (SmartTV)
  context?: string; // Contexte d'appel (obligatoire si liveScoreUrl)
  centerName?: string; // Nom du centre (obligatoire si liveScoreUrl)
  centerId?: string; // Identifiant du centre (obligatoire si liveScoreUrl)
  courtName?: string; // Nom du court (obligatoire si liveScoreUrl)
  courtId?: string; // Identifiant du court (obligatoire si liveScoreUrl)
}

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Valide les données de match fournies dans window.matchData
 * @param data - Données à valider
 * @returns Array des erreurs de validation (vide si tout est valide)
 */
export function validateMatchData(data: any): ValidationError[] {
  const errors: ValidationError[] = [];

  // Vérifier si les données existent
  if (!data) {
    errors.push({
      field: 'window.matchData',
      message: 'Aucune donnée de match trouvée dans window.matchData'
    });
    return errors;
  }

  // Champs toujours obligatoires
  const requiredFields: (keyof MatchInputData)[] = [
    'matchId',
    'competitionName',
    'categoryName',
    'drawName',
    // 'refereeName' est optionnel
    'dateTime',
    'scoringType',
    'playerA',
    'playerB',
    'resultCallbackUrl'
  ];

  // Vérifier la présence des champs obligatoires
  requiredFields.forEach(field => {
    if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
      errors.push({
        field,
        message: 'Ce champ est obligatoire et ne peut pas être vide'
      });
    }
  });

  // Champs obligatoires uniquement si liveScoreUrl est renseigné
  const hasLiveScoreUrl = data.liveScoreUrl && data.liveScoreUrl.trim() !== '';
  if (hasLiveScoreUrl) {
    const liveScoreRequiredFields: (keyof MatchInputData)[] = [
      'context',
      'centerName',
      'centerId',
      'courtName',
      'courtId'
    ];

    liveScoreRequiredFields.forEach(field => {
      if (!data[field] || (typeof data[field] === 'string' && data[field].trim() === '')) {
        errors.push({
          field,
          message: `Ce champ est obligatoire quand liveScoreUrl est renseigné`
        });
      }
    });
  }

  // Validation spécifique du format de date
  if (data.dateTime) {
    const dateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;
    if (!dateRegex.test(data.dateTime)) {
      errors.push({
        field: 'dateTime',
        message: 'Format attendu: "YYYY-MM-DD HH:MM" (ex: "2025-06-19 15:30")'
      });
    } else {
      // Vérifier que c'est une date valide
      const [datePart, timePart] = data.dateTime.split(' ');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      
      const date = new Date(year, month - 1, day, hour, minute);
      
      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        date.getHours() !== hour ||
        date.getMinutes() !== minute ||
        hour < 0 || hour > 23 ||
        minute < 0 || minute > 59
      ) {
        errors.push({
          field: 'dateTime',
          message: 'Date ou heure invalide'
        });
      }
    }
  }

  // Validation du scoringType
  if (data.scoringType && !['BestOf3', 'BestOf5'].includes(data.scoringType)) {
    errors.push({
      field: 'scoringType',
      message: 'Valeur autorisée: "BestOf3" ou "BestOf5"'
    });
  }

  // Validation de l'URL callback (format uniquement, tous protocoles acceptés)
  if (data.resultCallbackUrl) {
    try {
      new URL(data.resultCallbackUrl);
      // URL valide - tous protocoles acceptés (http, https, etc.)
    } catch {
      errors.push({
        field: 'resultCallbackUrl',
        message: 'Format d\'URL invalide'
      });
    }
  }

  // Validation de l'URL de redirection (optionnelle)
  if (data.redirectUrl && data.redirectUrl.trim() !== '') {
    try {
      new URL(data.redirectUrl);
    } catch {
      errors.push({
        field: 'redirectUrl',
        message: 'Format d\'URL de redirection invalide'
      });
    }
  }

  // Validation de l'URL liveScore (optionnelle)
  if (data.liveScoreUrl && data.liveScoreUrl.trim() !== '') {
    try {
      new URL(data.liveScoreUrl);
    } catch {
      errors.push({
        field: 'liveScoreUrl',
        message: 'Format d\'URL de live score invalide'
      });
    }
  }

  // Validation des URLs de branding (optionnelles)
  if (data.branding) {
    const brandingUrlFields = ['logo1', 'logo2', 'favicon'] as const;
    brandingUrlFields.forEach(field => {
      const url = data.branding[field];
      if (url && typeof url === 'string' && url.trim() !== '') {
        try {
          new URL(url);
        } catch {
          errors.push({
            field: `branding.${field}`,
            message: `Format d'URL invalide pour ${field}`
          });
        }
      }
    });

    // Validation des URLs des logos sponsors
    if (Array.isArray(data.branding.sponsorLogos)) {
      data.branding.sponsorLogos.forEach((sponsor: any, index: number) => {
        if (sponsor?.url && typeof sponsor.url === 'string' && sponsor.url.trim() !== '') {
          const url = sponsor.url.trim();
          // Accept relative URLs (starting with / or ./) or absolute URLs
          const isRelativeUrl = url.startsWith('/') || url.startsWith('./');
          if (!isRelativeUrl) {
            try {
              new URL(url);
            } catch {
              errors.push({
                field: `branding.sponsorLogos[${index}].url`,
                message: `Format d'URL invalide pour le logo sponsor #${index + 1}`
              });
            }
          }
        }
      });
    }
  }

  return errors;
}

/**
 * Convertit les données d'entrée validées vers le format MatchInfo de l'application
 * @param inputData - Données d'entrée validées
 * @returns Objet MatchInfo pour l'initialisation du state
 */
export function convertToMatchInfo(inputData: any) {
  return {
    matchId: inputData.matchId,
    competitionName: inputData.competitionName,
    categoryName: inputData.categoryName,
    drawName: inputData.drawName,
    refereeName: inputData.refereeName,
    dateTime: inputData.dateTime,
    scoringType: inputData.scoringType,
    playerA: inputData.playerA,
    playerB: inputData.playerB,
    playerAAvatar: inputData.playerAAvatar,
    playerBAvatar: inputData.playerBAvatar,
    resultCallbackUrl: inputData.resultCallbackUrl,
    redirectUrl: inputData.redirectUrl,
    roundType: inputData.roundType,
    branding: inputData.branding,
    // Champs live score (conditionnels)
    liveScoreUrl: inputData.liveScoreUrl,
    context: inputData.context,
    centerName: inputData.centerName,
    centerId: inputData.centerId,
    courtName: inputData.courtName,
    courtId: inputData.courtId
  };
}